import { useGetMarketingSiteData } from "InRangeAPI";
import AnalyseSiteMap from "components/composite/AnalyseSiteMap";
import RevealQuickAnalysis from "components/composite/RevealQuickAnalysis";
import SEO from "components/SEO";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { useLanguage } from "i18n/LanguageContext";

const CURRENCY_BY_LOCALE = {
  en: { symbol: "£", rate: 1, numberLocale: "en-US" },
  nl: { symbol: "€", rate: 1.134, numberLocale: "nl-NL" },
};

const getCurrencyConfig = (language) =>
  CURRENCY_BY_LOCALE[language] || CURRENCY_BY_LOCALE.en;

const buildDefaultValues = (currency) => ({
  generation: "...",
  avoidance: "...",
  landlordRevenue: `${currency.symbol}...`,
  tenantSavings: `${currency.symbol}...`,
});

const addCommas = (number, numberLocale) => {
  return number.toLocaleString(numberLocale, { maximumFractionDigits: 0 });
};

const createCurrencyRange = (value, range, currency) => {
  const converted = value * currency.rate;
  const bottomRange = Math.floor(converted - converted * range);
  const topRange = Math.ceil(converted + converted * range);
  return (
    currency.symbol +
    addCommas(bottomRange, currency.numberLocale) +
    " - " +
    currency.symbol +
    addCommas(topRange, currency.numberLocale)
  );
};

const formatValues = (values, currency) => {
  if (Object.keys(values).length === 0) return buildDefaultValues(currency);

  let newValues = structuredClone(values);

  newValues["generation"] = addCommas(
    newValues["generation"] / 1000,
    currency.numberLocale
  );
  newValues["avoidance"] = (newValues["avoidance"] / 1_000_000).toFixed(1);
  newValues["landlordRevenue"] = createCurrencyRange(
    newValues["landlordRevenue"],
    0.1,
    currency
  );
  newValues["tenantSavings"] = createCurrencyRange(
    newValues["tenantSavings"],
    0.1,
    currency
  );
  return newValues;
};

const AnalysePage = () => {
  const { latitude, longitude } = useParams();
  const { t } = useTranslation("analyse");
  const { language } = useLanguage();
  const currency = getCurrencyConfig(language);
  const initialLatLong = `${longitude},${latitude}`;
  const [buildingPoint, setBuildingPoint] = useState([latitude, longitude]);
  const [buildingPostcodeAddress, setBuildingPostcodeAddress] = useState();
  const [totalBuildingArea, setTotalBuildingArea] = useState();
  const [buildingKey, setBuildingKey] = useState();
  const [values, setValues] = useState(buildDefaultValues(currency));
  const [formValues, setFormValues] = useState();
  const [claims, setClaims] = useState({});
  const [requests, setRequests] = useState({});
  const [buildingValidity, setBuildingValidity] = useState({
    size: true,
    location: true,
  });
  const [hidden, setHidden] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  const validBuilding = buildingValidity.size && buildingValidity.location;
  const fetchMarketingSiteData = useGetMarketingSiteData(
    buildingKey,
    totalBuildingArea,
    validBuilding
  );

  const setClaimed = (claimed) => {
    setClaims({ ...claims, [buildingKey]: claimed });
  };

  const setRequestedUpdate = (requestedUpdate) => {
    setRequests({ ...requests, [buildingKey]: requestedUpdate });
  };

  useEffect(() => {
    if (!validBuilding) {
      setDataLoading(false);
      setValues(buildDefaultValues(currency));
      setHidden(false);
      return;
    }

    if (!fetchMarketingSiteData.data) {
      setDataLoading(true);
      setValues(buildDefaultValues(currency));
      setHidden(false);
      return;
    }

    setDataLoading(false);
    const marketingSiteData = fetchMarketingSiteData.data.data;
    setValues(formatValues(marketingSiteData, currency));

    setHidden(!formSubmitted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    fetchMarketingSiteData.data,
    totalBuildingArea,
    formSubmitted,
    validBuilding,
    language,
  ]);

  return (
    <>
    <SEO title={t("meta.title")} description={t("meta.description")} />
    <AnalysePageView
      dataLoading={dataLoading}
      buildingPoint={buildingPoint}
      setBuildingPoint={setBuildingPoint}
      buildingPostcodeAddress={buildingPostcodeAddress}
      setBuildingPostcodeAddress={setBuildingPostcodeAddress}
      values={values}
      currencySymbol={currency.symbol}
      hidden={hidden}
      requestedUpdate={requests[buildingKey]}
      setRequestedUpdate={setRequestedUpdate}
      claimed={claims[buildingKey]}
      setClaimed={setClaimed}
      formSubmitted={formSubmitted}
      setFormSubmitted={setFormSubmitted}
      setTotalBuildingArea={setTotalBuildingArea}
      buildingKey={buildingKey}
      setBuildingKey={setBuildingKey}
      buildingValidity={buildingValidity}
      setBuildingValidity={setBuildingValidity}
      formValues={formValues}
      setFormValues={setFormValues}
      initialLatLong={initialLatLong}
    ></AnalysePageView>
    </>
  );
};

export default AnalysePage;

const AnalysePageView = ({
  buildingPoint,
  dataLoading,
  setBuildingPoint,
  setBuildingPostcodeAddress,
  buildingPostcodeAddress,
  values,
  currencySymbol,
  hidden,
  formSubmitted,
  setFormSubmitted,
  requestedUpdate,
  setRequestedUpdate,
  claimed,
  setClaimed,
  setTotalBuildingArea,
  buildingKey,
  setBuildingKey,
  buildingValidity,
  setBuildingValidity,
  formValues,
  setFormValues,
  initialLatLong,
}) => {
  return (
    <div className="flex flex-col analyse:grid analyse:grid-cols-2 justify-center container-screen">
      <div className="p-2 flex-shrink">
        <AnalyseSiteMap
          initialLatLong={initialLatLong}
          buildingPoint={buildingPoint}
          setBuildingPoint={setBuildingPoint}
          setBuildingPostcodeAddress={setBuildingPostcodeAddress}
          setTotalBuildingArea={setTotalBuildingArea}
          setBuildingKey={setBuildingKey}
          setValid={setBuildingValidity}
          formValues={formValues}
        ></AnalyseSiteMap>
      </div>
      <div className="p-2 flex-shrink-0">
        <div className="flex flex-col">
          <RevealQuickAnalysis
            buildingPoint={buildingPoint}
            buildingPostcodeAddress={buildingPostcodeAddress}
            dataLoading={dataLoading}
            formSubmitted={formSubmitted}
            setFormSubmitted={setFormSubmitted}
            buildingValidity={buildingValidity}
            claimed={claimed}
            setClaimed={setClaimed}
            values={values}
            currencySymbol={currencySymbol}
            hidden={hidden}
            formValues={formValues}
            setFormValues={setFormValues}
            buildingKey={buildingKey}
            requestedUpdate={requestedUpdate}
            setRequestedUpdate={setRequestedUpdate}
          />
        </div>
      </div>
    </div>
  );
};
